import { TFunction } from 'i18next';
import {
    REALIZED_FIELD,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
} from 'routes/BudgetModule/config/constants';
import { AvailableCadrageMetricsT } from './type';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';

const YEAR_OFFSET_N_0 = 0;
const YEAR_OFFSET_N_1 = 1;
const YEAR_OFFSET_N_2 = 2;

const getTranslatedKey = (
    exercice: number | null,
    offset: number,
    t: TFunction,
    key: string,
): string => {
    const year = exercice == null ? ' - ' : exercice - offset;
    return t(key, { exercice: year });
};

/**
 * Generates a list of available cadrage metrics for a given exercice.
 *
 * This function creates an array of objects representing the available
 * cadrage metrics. Each object contains a `key` (which is the translated
 * string using the `t` function), a `metric` identifier, and a `yearOffset`.
 *
 * @returns An array of available cadrage metrics
 * that includes the translated key, metric, and yearOffset for each metric.
 */
export const getAvailableCadrageMetrics = (
    exercice: number | null,
    t: TFunction,
): AvailableCadrageMetricsT[] => [
    {
        key: getTranslatedKey(
            exercice,
            YEAR_OFFSET_N_2,
            t,
            'adminBudgetSpace.cadrage.metrics.amountRealized',
        ),
        metric: REALIZED_FIELD,
        yearOffset: YEAR_OFFSET_N_2,
    },
    {
        key: getTranslatedKey(
            exercice,
            YEAR_OFFSET_N_1,
            t,
            'adminBudgetSpace.cadrage.metrics.amountVoted',
        ),
        metric: VOTED_FIELD,
        yearOffset: YEAR_OFFSET_N_1,
    },
    {
        key: getTranslatedKey(
            exercice,
            YEAR_OFFSET_N_1,
            t,
            'adminBudgetSpace.cadrage.metrics.fullAmount',
        ),
        metric: VOTED_TOTAL_FIELD,
        yearOffset: YEAR_OFFSET_N_1,
    },
    {
        key: getTranslatedKey(
            exercice,
            YEAR_OFFSET_N_1,
            t,
            'adminBudgetSpace.cadrage.metrics.amountRealized',
        ),
        metric: REALIZED_FIELD,
        yearOffset: YEAR_OFFSET_N_1,
    },
    {
        key: getTranslatedKey(
            exercice,
            YEAR_OFFSET_N_0,
            t,
            'adminBudgetSpace.cadrage.metrics.amountVoted',
        ),
        metric: VOTED_FIELD,
        yearOffset: YEAR_OFFSET_N_0,
    },
];

/**
 * Triggers a tracker event when user select a new exercice.
 */
export const trackEventNewCadrageExerciceSelected = (exercice: number) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.exercice, {
        exercice: exercice,
    });

/**
 * Triggers a tracker event when user select a new metric.
 */
export const trackEventNewCadrageMetricSelected = (metric: string) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.metric, {
        metric: metric,
    });

/**
 * Triggers a tracker event when user click on the cadrage per chapter toggle.
 */
export const trackEventClickCadrageByChapterToggle = (
    activateCadrageByChapter: boolean,
) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.byChapter, {
        activateCadrageByChapter: activateCadrageByChapter,
    });

/**
 * Triggers a tracker event when user click on the cadrage per chapter button in D/R.
 */
export const trackEventClickCadrageByChapterGraph = () =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.byChapterGraph);
